import { CatmullRomCurve3, CurveType, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils.js';

export interface SurfacePathCurvePoints {
  outer: Vector3;
  inner: Vector3;
  path: Vector3;
  normal: Vector3;
  tangent: Vector3;
}

export interface SurfacePathCurvePointsArray {
  outers: Vector3[];
  inners: Vector3[];
  points: Vector3[];
  normals: Vector3[];
  tangents: Vector3[];
}
const EPSILON = 0.001;
/**
 * A curve that follows the surface of a 3D model.
 */
export class SurfacePathCurve extends CatmullRomCurve3 {
  offset = 0.5;

  uuid = '';
  /**
   * Create a surface path curve. This curve follows the surface of a 3D model.
   *
   * @param contour - Contours of the surface
   * @param curveType - Type of the curve. Default centripetal
   * @param tension - Tension of the curve. Expects a Float. Default 0.5
   */
  constructor(
    /**
     *  Contours of the surface
     */
    public contour: Vector3[],
    curveType: CurveType = 'centripetal',
    tension = 0.5,
    offset = 0.5
  ) {
    super(contour, true, curveType, tension);
    this.uuid = generateUUID();
    this.offset = offset;
  }

  /**
   * Get the normal at a given position on the surface.
   * @returns
   */
  getNormalAt(u: number, optionalTarget?: Vector3): Vector3 {
    const normal = new Vector3();
    const tangent = this.getTangentAt(u);

    const initial = this.getTangentAt(1 - EPSILON);

    let min = Number.MAX_VALUE;
    const tx = Math.abs(initial.x);
    const ty = Math.abs(initial.y);
    const tz = Math.abs(initial.z);

    if (tx <= min) {
      min = tx;
      normal.set(1, 0, 0);
    }

    if (ty <= min) {
      min = ty;
      normal.set(0, 1, 0);
    }

    if (tz <= min) {
      normal.set(0, 0, 1);
    }

    return optionalTarget ?? new Vector3().crossVectors(normal, tangent).normalize();
  }

  getTangent(t: number, optionalTarget?: Vector3): Vector3 {
    const delta = 0.0001;
    let t1 = t - delta;
    let t2 = t + delta;

    if (t1 < 0) t1 = 0;
    if (t2 > 1) t2 = 1;

    const ptr1 = this.getPoint(t1);
    const ptr2 = this.getPoint(t2);

    const tangent = ptr2.sub(ptr1).normalize();

    return (optionalTarget ?? new Vector3()).copy(tangent);
  }

  getPointsAt(t: number): SurfacePathCurvePoints {
    const outer = this.getOuterPointAt(t);
    const inner = this.getInnerPointAt(t);
    const path = this.getPointAt(t);
    const normal = this.getNormalAt(t);
    const tangent = this.getTangentAt(t);

    return { outer, inner, path, normal, tangent };
  }

  getPointsArray(divisions = 5): SurfacePathCurvePointsArray {
    const pointsArray: SurfacePathCurvePointsArray = {
      outers: [],
      inners: [],
      points: [],
      normals: [],
      tangents: [],
    };

    for (let d = 0; d <= divisions; d++) {
      const t = d / divisions;
      const points = this.getPointsAt(t);

      pointsArray.outers.push(points.outer);
      pointsArray.inners.push(points.inner);
      pointsArray.points.push(points.path);
      pointsArray.normals.push(points.normal);
      pointsArray.tangents.push(points.tangent);
    }

    return pointsArray;
  }

  /**
   * Get the offet point at a given position
   */
  getOffsetPointAt(u: number, offset: number, optionalTarget?: Vector3): Vector3 {
    const point = this.getPointAt(u);
    const normal = this.getNormalAt(u);

    point.add(normal.multiplyScalar(offset));

    return (optionalTarget ?? new Vector3()).copy(point);
  }

  getOuterPointAt(u: number, optionalTarget?: Vector3): Vector3 {
    return this.getOffsetPointAt(u, Math.abs(this.offset) * 0.5, optionalTarget);
  }

  getInnerPointAt(u: number, optionalTarget?: Vector3): Vector3 {
    return this.getOffsetPointAt(u, Math.abs(this.offset) * -0.5, optionalTarget);
  }

  toJSON(): { uuid: string; type: string } {
    return {
      uuid: this.uuid,
      type: 'SurfacePathCurve',
    };
  }
}
